import React, { useState, useEffect } from 'react';
import { Save, Trash2, X, FileJson } from 'lucide-react';
import api from '../api/client';
import { SavedServer, Track, Car } from '../types';
import CarSelectionTable from './CarSelectionTable';
import './ServerEditor.css';

interface ServerEditorProps {
  server: SavedServer | null;
  isNew: boolean;
  existingPorts?: number[];
  onSave: (server: SavedServer) => void;
  onDelete?: () => void;
  onCancel: () => void;
  onRefreshServer?: (serverId: string) => Promise<void> | void;
}

const ServerEditor: React.FC<ServerEditorProps> = ({
  server,
  isNew,
  existingPorts = [],
  onSave,
  onDelete,
  onCancel,
  onRefreshServer,
}) => {
  const [raceTracks, setRaceTracks] = useState<Track[]>([]);

  const parseScheduledStartValue = (dateTime: string, timezone: string): { date: string; time: string } | null => {
    if (!dateTime) return null;

    const absoluteTime = /[Zz]$|[+-][0-9]{2}:[0-9]{2}$/.test(dateTime);
    if (absoluteTime) {
      const date = new Date(dateTime);
      if (Number.isNaN(date.getTime())) return null;

      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      const parts = formatter.formatToParts(date);
      const year = parts.find((p) => p.type === 'year')?.value;
      const month = parts.find((p) => p.type === 'month')?.value;
      const day = parts.find((p) => p.type === 'day')?.value;
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;

      if (year && month && day && hour && minute) {
        return { date: `${year}-${month}-${day}`, time: `${hour}:${minute}` };
      }

      return null;
    }

    const [dateString, timeString] = dateTime.split('T');
    if (!dateString || !timeString) return null;

    return { date: dateString, time: timeString.split('.')[0].slice(0, 5) };
  };
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPracticeServer, setIsPracticeServer] = useState(false);

  const nextAvailablePort = () => {
    const taken = new Set(existingPorts);
    for (let p = 9700; p <= 9799; p++) {
      if (!taken.has(p)) return p;
    }
    return 9700;
  };

  // Server config state
  const [serverName, setServerName] = useState('');
  const [tcpPort, setTcpPort] = useState(() => isNew ? nextAvailablePort() : 9700);
  const [udpPort, setUdpPort] = useState(() => isNew ? nextAvailablePort() : 9700);
  const [portInput, setPortInput] = useState(() => String(isNew ? nextAvailablePort() : 9700));
  const [maxPlayers, setMaxPlayers] = useState(32);
  const [cycle, setCycle] = useState(true);
  const [driverPassword, setDriverPassword] = useState('');
  const [spectatorPassword, setSpectatorPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [serverType, setServerType] = useState('MultiplayerServerListSessionType_RANKED');
  const [selectedCars, setSelectedCars] = useState<string[]>([]);
  const [carConfigs, setCarConfigs] = useState<Map<string, { ballast: number; restrictor: number }>>(new Map());

  // Track selection
  const [selectedTrack, setSelectedTrack] = useState<string>('');
  const [selectedLayout, setSelectedLayout] = useState<string>('');

  // Session configs
  // durations are shown to the user in minutes; convert to seconds when saving
  const [practiceDuration, setPracticeDuration] = useState(60);
  const [practiceHour, setPracticeHour] = useState(16);
  const [practiceMinute, setPracticeMinute] = useState(0);
  const [practiceMaxWaitBox, setPracticeMaxWaitBox] = useState(10);
  const [practiceOvertimeWait, setPracticeOvertimeWait] = useState(30);

  const [qualifyDuration, setQualifyDuration] = useState(15);
  const [qualifyHour, setQualifyHour] = useState(16);
  const [qualifyMinute, setQualifyMinute] = useState(0);
  const [qualifyMaxWaitBox, setQualifyMaxWaitBox] = useState(10);
  const [qualifyOvertimeWait, setQualifyOvertimeWait] = useState(10);

  const [warmupDuration, setWarmupDuration] = useState(0);
  const [warmupHour, setWarmupHour] = useState(16);
  const [warmupMinute, setWarmupMinute] = useState(0);
  const [warmupMaxWaitBox, setWarmupMaxWaitBox] = useState(10);
  const [warmupOvertimeWait, setWarmupOvertimeWait] = useState(10);

  const [raceDuration, setRaceDuration] = useState(30);
  const [raceHour, setRaceHour] = useState(16);
  const [raceMinute, setRaceMinute] = useState(0);
  const [raceDurationType, setRaceDurationType] = useState<'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS'>('GameModeSelectionDuration_TIME');
  const [raceMaxWaitBox, setRaceMaxWaitBox] = useState(10);
  const [raceOvertimeWait, setRaceOvertimeWait] = useState(10);
  const [minWaitingPlayers, setMinWaitingPlayers] = useState(10);
  const [maxWaitingPlayers, setMaxWaitingPlayers] = useState(30);
  const [mandatoryPitStop, setMandatoryPitStop] = useState(false);
  const [requiresTyreChange, setRequiresTyreChange] = useState(false);
  const [requiresRefuelling, setRequiresRefuelling] = useState(false);
  const [pitWindow, setPitWindow] = useState(0);

  // Weather and player settings
  const [weatherType, setWeatherType] = useState('GameModeSelectionWeatherType_CLEAR');
  const [weatherBehaviour, setWeatherBehaviour] = useState('GameModeSelectionWeatherBehaviour_DYNAMIC');
  const [initialGrip, setInitialGrip] = useState('InitialGrip_GREEN');
  const [showJsonViewer, setShowJsonViewer] = useState(false);

  const [championshipName, setChampionshipName] = useState('');

  // Scheduled start
  const [scheduledStartEnabled, setScheduledStartEnabled] = useState(false);
  const [scheduledStartDate, setScheduledStartDate] = useState('');
  const [scheduledStartTime, setScheduledStartTime] = useState('12:00');
  const [scheduledStartTimezone, setScheduledStartTimezone] = useState('Europe/Paris');
  const [rotationLoading, setRotationLoading] = useState(false);
  const [rotationMessage, setRotationMessage] = useState('');
  const [rotationError, setRotationError] = useState('');

  useEffect(() => {
    const loadAssets = async () => {
      setLoading(true);
      try {
        const trackApi = isPracticeServer ? api.getPracticeTracks() : api.getRaceTracks();
        const [carRes, trackRes] = await Promise.all([
          api.getCars(),
          trackApi,
        ]);
        setCars(carRes.data || []);
        setRaceTracks(trackRes.data || []);

        // Only set default track if this is a new server and no server is being edited
        if (!server && trackRes.data && trackRes.data.length > 0) {
          setSelectedTrack(trackRes.data[0].track);
          setSelectedLayout(trackRes.data[0].layout);
        }
      } catch (err) {
        console.error('Failed to load assets:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAssets();
  }, [isPracticeServer, server]);

  useEffect(() => {
    const loadChampionship = async () => {
      if (!server?.championshipId) {
        setChampionshipName('');
        return;
      }

      try {
        const res = await api.getChampionships();
        const championships = res.data?.championships || [];
        const championship = championships.find((champ: any) => champ.id === server.championshipId);
        setChampionshipName(championship?.name || 'Unknown Championship');
      } catch (err) {
        console.error('Failed to load championship details:', err);
        setChampionshipName('Unknown Championship');
      }
    };

    loadChampionship();
  }, [server?.championshipId]);

  useEffect(() => {
    if (!server) {
      return;
    }

    setServerName(server.serverConfig.server_name || '');
    const loadedPort = server.serverConfig.server_tcp_listener_port || 9700;
    setTcpPort(loadedPort);
    setUdpPort(server.serverConfig.server_udp_listener_port || 9700);
    setPortInput(String(loadedPort));
    setMaxPlayers(server.serverConfig.max_players || 32);
    setCycle(server.serverConfig.cycle !== false);
    setDriverPassword(server.serverConfig.driver_password || '');
    setSpectatorPassword(server.serverConfig.spectator_password || '');
    setAdminPassword(server.serverConfig.admin_password || '');
    setServerType(server.serverConfig.type || 'MultiplayerServerListSessionType_RANKED');
    setSelectedCars((server.serverConfig.allowed_cars_list_full ?? []).map((c: any) => c.car_name));

    // Load car configs (ballast and restrictor)
    const configMap = new Map<string, { ballast: number; restrictor: number }>();
    (server.serverConfig.allowed_cars_list_full ?? []).forEach((c: any) => {
      configMap.set(c.car_name, { ballast: c.ballast || 0, restrictor: c.restrictor || 0 });
    });
    setCarConfigs(configMap);

    // Preserve selected track and layout from existing server
    setSelectedTrack(server.seasonConfig.event.track);
    setSelectedLayout(server.seasonConfig.event.layout);

    // Set practice server flag based on game_type
    const isPractice = server.seasonConfig.game_type === 'GameModeType_PRACTICE';
    setIsPracticeServer(isPractice);

    const gc = server.seasonConfig.game_config;
    // convert seconds -> minutes for UI
    setPracticeDuration(Math.round((gc.practice_duration || 0) / 60));
    setPracticeHour(gc.practice_time_of_day.hour);
    setPracticeMinute(gc.practice_time_of_day.minute);
    setPracticeMaxWaitBox(gc.practice_max_wait_to_box);
    setPracticeOvertimeWait(gc.practice_overtime_waiting_next_session);

    setQualifyDuration(Math.round((gc.qualify_duration || 0) / 60));
    setQualifyHour(gc.qualify_time_of_day.hour);
    setQualifyMinute(gc.qualify_time_of_day.minute);
    setQualifyMaxWaitBox(gc.qualify_max_wait_to_box);
    setQualifyOvertimeWait(gc.qualify_overtime_waiting_next_session);

    setWarmupDuration(Math.round((gc.warmup_duration || 0) / 60));
    setWarmupHour(gc.warmup_time_of_day.hour);
    setWarmupMinute(gc.warmup_time_of_day.minute);
    setWarmupMaxWaitBox(gc.warmup_max_wait_to_box);
    setWarmupOvertimeWait(gc.warmup_overtime_waiting_next_session);

    // race duration may be time (seconds) or laps (count)
    if ((gc.race_duration_type || 'GameModeSelectionDuration_TIME') === 'GameModeSelectionDuration_TIME') {
      setRaceDuration(Math.round((gc.race_duration || 0) / 60));
    } else {
      setRaceDuration(gc.race_duration || 0);
    }
    setRaceHour(gc.race_time_of_day.hour);
    setRaceMinute(gc.race_time_of_day.minute);
    setRaceDurationType((gc.race_duration_type || 'GameModeSelectionDuration_TIME') as 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS');
    setRaceMaxWaitBox(gc.race_max_wait_to_box);
    setRaceOvertimeWait(gc.race_overtime_waiting_next_session);
    setMandatoryPitStop(gc.mandatory_pit_stop === true);
    setRequiresTyreChange(gc.requires_tyre_change === true);
    setRequiresRefuelling(gc.requires_refuelling === true);
    setPitWindow(gc.pit_window ?? 0);

    setWeatherType(server.seasonConfig.weather_type);
    setWeatherBehaviour(server.seasonConfig.weather_behaviour);
    setInitialGrip(server.seasonConfig.initial_grip);

    setMinWaitingPlayers(gc.min_waiting_for_players);
    setMaxWaitingPlayers(gc.max_waiting_for_players);

    // Load scheduled start settings if available
    if (server.scheduledStart?.enabled) {
      setScheduledStartEnabled(true);
      setScheduledStartTimezone(server.scheduledStart.timezone);

      const parsed = parseScheduledStartValue(server.scheduledStart.dateTime, server.scheduledStart.timezone || 'UTC');
      if (parsed) {
        setScheduledStartDate(parsed.date);
        setScheduledStartTime(parsed.time);
      }
    }
  }, [server]);

  // Reset form to original server state or defaults
  const resetFormFromServer = () => {
    if (server) {
      setServerName(server.serverConfig.server_name || '');
      const resetPort = server.serverConfig.server_tcp_listener_port || 9700;
      setTcpPort(resetPort);
      setUdpPort(server.serverConfig.server_udp_listener_port || 9700);
      setPortInput(String(resetPort));
      setMaxPlayers(server.serverConfig.max_players || 32);
      setCycle(server.serverConfig.cycle !== false);
      setDriverPassword(server.serverConfig.driver_password || '');
      setSpectatorPassword(server.serverConfig.spectator_password || '');
      setAdminPassword(server.serverConfig.admin_password || '');
      setServerType(server.serverConfig.type || 'MultiplayerServerListSessionType_RANKED');
      setSelectedCars((server.serverConfig.allowed_cars_list_full ?? []).map((c: any) => c.car_name));

      const configMap = new Map<string, { ballast: number; restrictor: number }>();
      (server.serverConfig.allowed_cars_list_full ?? []).forEach((c: any) => {
        configMap.set(c.car_name, { ballast: c.ballast || 0, restrictor: c.restrictor || 0 });
      });
      setCarConfigs(configMap);

      setSelectedTrack(server.seasonConfig.event.track);
      setSelectedLayout(server.seasonConfig.event.layout);

      const isPractice = server.seasonConfig.game_type === 'GameModeType_PRACTICE';
      setIsPracticeServer(isPractice);

      const gc = server.seasonConfig.game_config;
      // convert seconds -> minutes for UI
      setPracticeDuration(Math.round((gc.practice_duration || 0) / 60));
      setPracticeHour(gc.practice_time_of_day.hour);
      setPracticeMinute(gc.practice_time_of_day.minute);
      setPracticeMaxWaitBox(gc.practice_max_wait_to_box);
      setPracticeOvertimeWait(gc.practice_overtime_waiting_next_session);

      setQualifyDuration(Math.round((gc.qualify_duration || 0) / 60));
      setQualifyHour(gc.qualify_time_of_day.hour);
      setQualifyMinute(gc.qualify_time_of_day.minute);
      setQualifyMaxWaitBox(gc.qualify_max_wait_to_box);
      setQualifyOvertimeWait(gc.qualify_overtime_waiting_next_session);

      setWarmupDuration(Math.round((gc.warmup_duration || 0) / 60));
      setWarmupHour(gc.warmup_time_of_day.hour);
      setWarmupMinute(gc.warmup_time_of_day.minute);
      setWarmupMaxWaitBox(gc.warmup_max_wait_to_box);
      setWarmupOvertimeWait(gc.warmup_overtime_waiting_next_session);

      if ((gc.race_duration_type || 'GameModeSelectionDuration_TIME') === 'GameModeSelectionDuration_TIME') {
        setRaceDuration(Math.round((gc.race_duration || 0) / 60));
      } else {
        setRaceDuration(gc.race_duration || 0);
      }
      setRaceHour(gc.race_time_of_day.hour);
      setRaceMinute(gc.race_time_of_day.minute);
      setRaceDurationType((gc.race_duration_type || 'GameModeSelectionDuration_TIME') as 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS');
      setRaceMaxWaitBox(gc.race_max_wait_to_box);
      setRaceOvertimeWait(gc.race_overtime_waiting_next_session);
      setMandatoryPitStop(gc.mandatory_pit_stop === true);
      setRequiresTyreChange(gc.requires_tyre_change === true);
      setRequiresRefuelling(gc.requires_refuelling === true);
      setPitWindow(gc.pit_window ?? 0);

      setWeatherType(server.seasonConfig.weather_type);
      setWeatherBehaviour(server.seasonConfig.weather_behaviour);
      setInitialGrip(server.seasonConfig.initial_grip);

      setMinWaitingPlayers(gc.min_waiting_for_players);
      setMaxWaitingPlayers(gc.max_waiting_for_players);

      if (server.scheduledStart?.enabled) {
        setScheduledStartEnabled(true);
        setScheduledStartTimezone(server.scheduledStart.timezone);

        const parsed = parseScheduledStartValue(server.scheduledStart.dateTime, server.scheduledStart.timezone || 'UTC');
        if (parsed) {
          setScheduledStartDate(parsed.date);
          setScheduledStartTime(parsed.time);
        }
      } else {
        setScheduledStartEnabled(false);
        setScheduledStartDate('');
        setScheduledStartTime('12:00');
      }
    }
  };

  const formatRotationPlanDateTime = (dateTime: string, timezone: string) => {
    if (!dateTime) return '';
    const [datePart, timePart] = dateTime.split('T');
    if (!datePart || !timePart) return dateTime;
    const time = timePart.split('.')[0];
    return `${datePart} ${time} (${timezone})`;
  };

  const checkRotationScheduleConflict = () => {
    if (!server?.rotationPlan?.enabled || !server.rotationPlan.dateTime) {
      return null;
    }

    const rotationDateTime = server.rotationPlan.dateTime;
    const nextScheduledStart = server.rotationPlan.nextScheduledStart?.dateTime;

    if (!nextScheduledStart) {
      return null;
    }

    // Compare dates (both are ISO strings)
    if (new Date(nextScheduledStart) < new Date(rotationDateTime)) {
      return {
        rotationTime: formatRotationPlanDateTime(rotationDateTime, server.rotationPlan.timezone || 'UTC'),
        scheduledStartTime: formatRotationPlanDateTime(nextScheduledStart, server.rotationPlan.nextScheduledStart?.timezone || 'UTC'),
      };
    }

    return null;
  };

  const handleRotateNow = async () => {
    if (!server) {
      return;
    }

    setRotationLoading(true);
    setRotationMessage('');
    setRotationError('');

    try {
      await api.rotateServerNow(server.id);
      setRotationMessage('Rotation plan applied successfully. Server configuration has been updated.');

      // Refresh both the server list and the selected server details
      if (onRefreshServer) {
        await onRefreshServer(server.id);
      }
    } catch (err: any) {
      setRotationError(err.response?.data?.error || err.message || 'Failed to rotate server now');
    } finally {
      setRotationLoading(false);
    }
  };

  const handleSyncNow = async () => {
    if (!server) {
      return;
    }

    setRotationLoading(true);
    setRotationMessage('');
    setRotationError('');

    try {
      await api.syncServerNow(server.id);
      setRotationMessage('Server configuration synced to the upcoming event.');

      // Refresh both the server list and the selected server details
      if (onRefreshServer) {
        await onRefreshServer(server.id);
      }
    } catch (err: any) {
      setRotationError(err.response?.data?.error || err.message || 'Failed to sync server now');
    } finally {
      setRotationLoading(false);
    }
  };

  const handleCancel = () => {
    resetFormFromServer();
    onCancel();
  };

  const handleSave = () => {
    const dataToSave: SavedServer = {
      id: server?.id || '',
      name: serverName,
      serverConfig: {
        server_tcp_listener_port: tcpPort,
        server_udp_listener_port: udpPort,
        server_tcp_internal_port: tcpPort,
        server_udp_internal_port: udpPort,
        server_http_port: 8080,
        server_name: serverName,
        max_players: maxPlayers,
        cycle,
        allowed_cars_list_full: selectedCars.map((carName) => ({
          car_name: carName,
          ballast: carConfigs.get(carName)?.ballast || 0,
          restrictor: carConfigs.get(carName)?.restrictor || 0.0,
        })),
        driver_password: driverPassword,
        spectator_password: spectatorPassword,
        admin_password: adminPassword,
        type: serverType,
        entry_list_path: '',
        results_path: '', // Will be set by backend from .env
      },
      seasonConfig: {
        game_type: isPracticeServer ? 'GameModeType_PRACTICE' : 'GameModeType_RACE_WEEKEND',
        event: {
          track: selectedTrack,
          layout: selectedLayout,
          event_name: raceTracks.find((t) => t.track === selectedTrack && t.layout === selectedLayout)?.event_name || '',
          track_length: raceTracks.find((t) => t.track === selectedTrack && t.layout === selectedLayout)?.track_length.toString() || '',
        },
        export_json: false,
          game_config: {
          // convert minutes (UI) -> seconds for JSON
          practice_duration: (practiceDuration || 0) * 60,
          practice_time_of_day: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
            hour: practiceHour,
            minute: practiceMinute,
            second: 0,
            time_multiplier: 1,
          },
          practice_overtime_waiting_next_session: practiceOvertimeWait,
          practice_max_wait_to_box: practiceMaxWaitBox,
          qualify_duration: (qualifyDuration || 0) * 60,
          qualify_time_of_day: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
            hour: qualifyHour,
            minute: qualifyMinute,
            second: 0,
            time_multiplier: 1,
          },
          qualify_overtime_waiting_next_session: qualifyOvertimeWait,
          qualify_max_wait_to_box: qualifyMaxWaitBox,
          warmup_duration: (warmupDuration || 0) * 60,
          warmup_time_of_day: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
            hour: warmupHour,
            minute: warmupMinute,
            second: 0,
            time_multiplier: 1,
          },
          warmup_overtime_waiting_next_session: warmupOvertimeWait,
          warmup_max_wait_to_box: warmupMaxWaitBox,
          race_duration: (raceDuration || 0) * (raceDurationType === 'GameModeSelectionDuration_TIME' ? 60 : 1),
          race_duration_type: raceDurationType,
          race_time_of_day: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
            hour: raceHour,
            minute: raceMinute,
            second: 0,
            time_multiplier: 1,
          },
          race_overtime_waiting_next_session: raceOvertimeWait,
          race_max_wait_to_box: raceMaxWaitBox,
          min_waiting_for_players: minWaitingPlayers,
          max_waiting_for_players: maxWaitingPlayers,
          ...(mandatoryPitStop && raceDurationType === 'GameModeSelectionDuration_TIME' && raceDuration * 60 >= 1200
            ? {
                mandatory_pit_stop: true,
                requires_tyre_change: requiresTyreChange,
                requires_refuelling: requiresRefuelling,
                pit_window: Math.max(0, Math.min(Math.round(pitWindow), raceDuration * 60)),
              }
            : {}),
        },
        weather_type: weatherType,
        weather_behaviour: weatherBehaviour,
        initial_grip: initialGrip,
      },
      createdAt: server?.createdAt || Date.now(),
      status: server?.status || 'stopped',
      processId: server?.processId || null,
      scheduledStart: scheduledStartEnabled
        ? {
            enabled: true,
            dateTime: `${scheduledStartDate}T${scheduledStartTime}:00`,
            timezone: scheduledStartTimezone,
          }
        : {
            enabled: false,
            dateTime: '',
            timezone: scheduledStartTimezone,
          },
    };
    onSave(dataToSave);
  };

  const handleCarSelectionChange = (carName: string, selected: boolean) => {
    if (selected) {
      setSelectedCars((prev) => [...prev, carName]);
      // Initialize config if not exists
      if (!carConfigs.has(carName)) {
        const newConfigs = new Map(carConfigs);
        newConfigs.set(carName, { ballast: 0, restrictor: 0 });
        setCarConfigs(newConfigs);
      }
    } else {
      setSelectedCars((prev) => prev.filter((c) => c !== carName));
    }
  };

  const handleCarConfigChange = (carName: string, ballast: number, restrictor: number) => {
    const newConfigs = new Map(carConfigs);
    newConfigs.set(carName, { ballast, restrictor });
    setCarConfigs(newConfigs);
  };

  const selectedTrackObj = raceTracks.find(
    (t) => t.track === selectedTrack && t.layout === selectedLayout
  );

  const uniqueTracks = Array.from(new Map(raceTracks.map((t) => [t.track, t])).values());
  const layoutsForTrack = raceTracks.filter((t) => t.track === selectedTrack);

  if (loading) {
    return <div className="editor-loading">Loading...</div>;
  }

  return (
    <div className="server-editor">
      <div className="editor-header">
        <h2>{isNew ? 'New Server' : `Edit: ${serverName}`}</h2>
        <div className="header-buttons">
          {!isNew && (
            <button className="view-json-button" onClick={() => setShowJsonViewer(true)} title="View JSON config files">
              <FileJson size={20} />
            </button>
          )}
          <button className="close-button" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="editor-content">
        <div className="editor-scroll">
          {/* Server Information */}
          <div className="section">
            <h3>Server Information</h3>

            {!isNew && server && (
              <div className="server-id-box">
                <label>Server ID (UUID)</label>
                <div className="server-id-display">{server.id}</div>
              </div>
            )}

            {server?.championshipId && (
              <div className="championship-summary">
                <div className="championship-summary-row">
                  <div>
                    <div className="summary-label">Championship ID</div>
                    <div className="summary-value">{server.championshipId}</div>
                  </div>
                  {championshipName && (
                    <div>
                      <div className="summary-label">Championship</div>
                      <div className="summary-value">{championshipName}</div>
                    </div>
                  )}
                </div>

                {server.rotationPlan?.enabled && server.rotationPlan.dateTime ? (
                  <div className="rotation-plan-box">
                    <div>
                      <div className="summary-label">Next rotation</div>
                      <div className="summary-value">
                        Rotates to <strong>{server.rotationPlan.nextSeasonConfig?.event.track + ' - ' + server.rotationPlan.nextSeasonConfig?.event.layout || server.rotationPlan.nextServerName || 'the next event'}</strong> at <strong>{formatRotationPlanDateTime(server.rotationPlan.dateTime, server.rotationPlan.timezone || 'UTC')}</strong>
                      </div>
                      {(() => {
                        const conflict = checkRotationScheduleConflict();
                        if (conflict) {
                          return (
                            <div style={{
                              marginTop: '12px',
                              padding: '8px 12px',
                              backgroundColor: 'var(--warning-bg, #fef3c7)',
                              border: '1px solid var(--warning-border, #fbbf24)',
                              borderRadius: '4px',
                              color: 'var(--warning-text, #92400e)',
                              fontSize: '0.9rem'
                            }}>
                              <strong>⚠️ Timing Conflict:</strong> The next scheduled event starts at <strong>{conflict.scheduledStartTime}</strong>, which is <strong>before</strong> this rotation at <strong>{conflict.rotationTime}</strong>. The server will not automatically start for the scheduled event.
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="rotation-buttons">
                      <button
                        className="rotate-now-button"
                        type="button"
                        disabled={rotationLoading}
                        onClick={handleRotateNow}
                      >
                        {rotationLoading ? 'Rotating…' : 'Rotate Now'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rotation-plan-box">
                    <div>
                      <div className="summary-label">Rotation</div>
                      <div className="summary-value">No active rotation plan configured.</div>
                    </div>
                  </div>
                )}
                <button
                        className="sync-now-button"
                        type="button"
                        disabled={rotationLoading}
                        onClick={handleSyncNow}
                      >
                        {rotationLoading ? 'Syncing…' : 'Sync Now'}
                </button>
                {rotationMessage && <div className="rotation-message success">{rotationMessage}</div>}
                {rotationError && <div className="rotation-message error">{rotationError}</div>}
              </div>
            )}

            <div className="form-group">
              <label>Server Name *</label>
              <input
                type="text"
                value={serverName}
                onChange={(e) => setServerName(e.target.value)}
                placeholder="e.g., SSRI - Monza GT3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>TCP/UDP Port (9700-9799) *</label>
                <input
                  type="number"
                  value={portInput}
                  onChange={(e) => {
                    setPortInput(e.target.value);
                    const port = parseInt(e.target.value, 10);
                    if (!isNaN(port) && port >= 9700 && port <= 9799) {
                      setTcpPort(port);
                      setUdpPort(port);
                    }
                  }}
                  onBlur={() => setPortInput(String(tcpPort))}
                  min="9700"
                  max="9799"
                />
              </div>
              <div className="form-group">
                <label>Max Players</label>
                <input
                  type="number"
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(Math.min(parseInt(e.target.value), selectedTrackObj?.max_pit_slot || 32))}
                  max={selectedTrackObj?.max_pit_slot || 32}
                  min="1"
                />
                {selectedTrackObj && <small>Max: {selectedTrackObj.max_pit_slot} (track pit slots)</small>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Driver Password</label>
                <input
                  type="password"
                  value={driverPassword}
                  onChange={(e) => setDriverPassword(e.target.value)}
                  placeholder="Leave empty for no password"
                />
              </div>
              <div className="form-group">
                <label>Spectator Password</label>
                <input
                  type="password"
                  value={spectatorPassword}
                  onChange={(e) => setSpectatorPassword(e.target.value)}
                  placeholder="Leave empty for no password"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Admin Password</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Leave empty for no password"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={cycle}
                  onChange={(e) => setCycle(e.target.checked)}
                />
                Enable Cycle (loop sessions)
              </label>
            </div>
          </div>

          {/* Track Selection */}
          <div className="section">
            <h3>Event Configuration</h3>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={isPracticeServer}
                  onChange={(e) => setIsPracticeServer(e.target.checked)}
                />
                Practice Server (only practice session, uses events_practice.json)
              </label>
              <small style={{ display: 'block', marginTop: '6px', color: '#94a3b8' }}>
                {isPracticeServer 
                  ? 'Server will run practice sessions only. Tracks loaded from events_practice.json'
                  : 'Server will run full race weekend (practice, qualifying, warmup, race) from events_race_weekend.json'}
              </small>
            </div>

            <hr style={{ margin: '12px 0', borderColor: '#334155' }} />

            <h4 style={{ marginTop: '12px', marginBottom: '12px' }}>Track Selection</h4>

            <div className="form-row">
              <div className="form-group">
                <label>Track *</label>
                <select
                  value={selectedTrack}
                  onChange={(e) => {
                    setSelectedTrack(e.target.value);
                    const firstLayout = raceTracks.find((t) => t.track === e.target.value);
                    if (firstLayout) {
                      setSelectedLayout(firstLayout.layout);
                    }
                  }}
                >
                  <option value="">Select a track...</option>
                  {uniqueTracks.map((track) => (
                    <option key={track.track} value={track.track}>
                      {track.track}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Layout *</label>
                <select
                  value={selectedLayout}
                  onChange={(e) => setSelectedLayout(e.target.value)}
                  disabled={!selectedTrack}
                >
                  <option value="">Select a layout...</option>
                  {layoutsForTrack.map((track) => (
                    <option key={`${track.track}|${track.layout}`} value={track.layout}>
                      {track.layout} ({track.track_length}m)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedTrackObj && (
              <div className="track-info">
                <p>
                  <strong>Track Length:</strong> {selectedTrackObj.track_length}m
                </p>
                <p>
                  <strong>Max Pit Slots:</strong> {selectedTrackObj.max_pit_slot}
                </p>
              </div>
            )}
          </div>

        <div className="section">
          <h3>Scheduled Start</h3>

          <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={scheduledStartEnabled}
                  onChange={(e) => setScheduledStartEnabled(e.target.checked)}
                />
                Enable Scheduled Start
              </label>
            </div>

            {scheduledStartEnabled && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={scheduledStartDate}
                      onChange={(e) => setScheduledStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="form-group">
                    <label>Start Time (HH:MM) *</label>
                    <input
                      type="time"
                      value={scheduledStartTime}
                      onChange={(e) => setScheduledStartTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={scheduledStartTimezone}
                    onChange={(e) => setScheduledStartTimezone(e.target.value)}
                  >
                    <option value="Europe/Paris">CET/CEST (Europe/Paris)</option>
                    <option value="Europe/London">GMT/BST (Europe/London)</option>
                    <option value="Europe/Berlin">CET/CEST (Europe/Berlin)</option>
                    <option value="Europe/Amsterdam">CET/CEST (Europe/Amsterdam)</option>
                    <option value="America/New_York">EST/EDT (America/New_York)</option>
                    <option value="America/Los_Angeles">PST/PDT (America/Los_Angeles)</option>
                    <option value="America/Chicago">CST/CDT (America/Chicago)</option>
                    <option value="Asia/Tokyo">JST (Asia/Tokyo)</option>
                    <option value="Australia/Sydney">AEDT/AEST (Australia/Sydney)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div className="info-box">
                  <p>
                    <strong>Scheduled for:</strong> {scheduledStartDate && scheduledStartTime
                      ? new Date(`${scheduledStartDate}T${scheduledStartTime}:00`).toLocaleString()
                      : 'Please select date and time'}
                  </p>
                  <p style={{ fontSize: '12px', marginTop: '8px', color: '#94a3b8' }}>
                    The server will automatically start at the configured date and time in the selected timezone.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Cars Selection */}
          <div className="section">
            <h3>Allowed Cars</h3>
            <CarSelectionTable
              cars={cars}
              selectedCars={selectedCars}
              carConfigs={carConfigs}
              onSelectionChange={handleCarSelectionChange}
              onConfigChange={handleCarConfigChange}
            />
          </div>

          {/* Weather & Players */}
          <div className="section">
            <h3>Weather & Players</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Weather Type</label>
                <select value={weatherType} onChange={(e) => setWeatherType(e.target.value)}>
                  <option value="GameModeSelectionWeatherType_CLEAR">Clear</option>
                  <option value="GameModeSelectionWeatherType_SCATTERED_CLOUDS">Scattered Clouds</option>
                  <option value="GameModeSelectionWeatherType_BROKEN_CLOUDS">Broken Clouds</option>
                  <option value="GameModeSelectionWeatherType_OVERCAST">Overcast</option>
                  <option value="GameModeSelectionWeatherType_DAMP">Damp</option>
                  <option value="GameModeSelectionWeatherType_DRIZZLE">Drizzle</option>
                  <option value="GameModeSelectionWeatherType_RAIN">Rain</option>
                  <option value="GameModeSelectionWeatherType_HEAVY_RAIN">Heavy Rain</option>
                </select>
              </div>
              <div className="form-group">
                <label>Weather Behaviour</label>
                <select value={weatherBehaviour} onChange={(e) => setWeatherBehaviour(e.target.value)}>
                  <option value="GameModeSelectionWeatherBehaviour_DYNAMIC">Dynamic</option>
                  <option value="GameModeSelectionWeatherBehaviour_STATIC">Static</option>
                </select>
              </div>
              <div className="form-group">
                <label>Initial Grip</label>
                <select value={initialGrip} onChange={(e) => setInitialGrip(e.target.value)}>
                  <option value="InitialGrip_GREEN">Green</option>
                  <option value="InitialGrip_FAST">Fast</option>
                  <option value="InitialGrip_OPTIMUM">Optimum</option>
                </select>
              </div>
            </div>
          </div>

          {/* Session Configurations */}
          <SessionSection label="Practice" duration={practiceDuration} setDuration={setPracticeDuration} hour={practiceHour} setHour={setPracticeHour} minute={practiceMinute} setMinute={setPracticeMinute} maxWaitBox={practiceMaxWaitBox} setMaxWaitBox={setPracticeMaxWaitBox} overtimeWait={practiceOvertimeWait} setOvertimeWait={setPracticeOvertimeWait} />
          {!isPracticeServer && (
            <>
              <SessionSection label="Qualify" duration={qualifyDuration} setDuration={setQualifyDuration} hour={qualifyHour} setHour={setQualifyHour} minute={qualifyMinute} setMinute={setQualifyMinute} maxWaitBox={qualifyMaxWaitBox} setMaxWaitBox={setQualifyMaxWaitBox} overtimeWait={qualifyOvertimeWait} setOvertimeWait={setQualifyOvertimeWait} />
              <SessionSection label="Warmup" duration={warmupDuration} setDuration={setWarmupDuration} hour={warmupHour} setHour={setWarmupHour} minute={warmupMinute} setMinute={setWarmupMinute} maxWaitBox={warmupMaxWaitBox} setMaxWaitBox={setWarmupMaxWaitBox} overtimeWait={warmupOvertimeWait} setOvertimeWait={setWarmupOvertimeWait} />
              <SessionSection 
                label="Race" 
                duration={raceDuration} 
                setDuration={setRaceDuration} 
                hour={raceHour} 
                setHour={setRaceHour} 
                minute={raceMinute} 
                setMinute={setRaceMinute} 
                maxWaitBox={raceMaxWaitBox} 
                setMaxWaitBox={setRaceMaxWaitBox} 
                overtimeWait={raceOvertimeWait} 
                setOvertimeWait={setRaceOvertimeWait}
                isRaceSession={true}
                durationType={raceDurationType}
                setDurationType={setRaceDurationType}
                minWaitingPlayers={minWaitingPlayers}
                setMinWaitingPlayers={setMinWaitingPlayers}
                maxWaitingPlayers={maxWaitingPlayers}
                setMaxWaitingPlayers={setMaxWaitingPlayers}
              />
              <PitStopRulesSection
                duration={raceDuration}
                durationType={raceDurationType}
                mandatoryPitStop={mandatoryPitStop}
                setMandatoryPitStop={setMandatoryPitStop}
                requiresTyreChange={requiresTyreChange}
                setRequiresTyreChange={setRequiresTyreChange}
                requiresRefuelling={requiresRefuelling}
                setRequiresRefuelling={setRequiresRefuelling}
                pitWindow={pitWindow}
                setPitWindow={setPitWindow}
              />
            </>
          )}
        </div>

        <div className="editor-actions">
          <button onClick={handleSave} className="save-button">
            <Save size={18} />
            {isNew ? 'Create Server' : 'Save Changes'}
          </button>
          {onDelete && (
            <button onClick={onDelete} className="delete-button">
              <Trash2 size={18} />
              Delete
            </button>
          )}
          <button onClick={handleCancel} className="cancel-button">
            Cancel
          </button>
        </div>
      </div>

      {/* JSON Viewer Modal */}
      {showJsonViewer && server && (
        <div className="modal-overlay" onClick={() => setShowJsonViewer(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>JSON Configuration</h3>
              <button className="modal-close" onClick={() => setShowJsonViewer(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="json-tabs">
                <div className="json-section">
                  <h4>server.json</h4>
                  <pre className="json-viewer">{JSON.stringify(server.serverConfig, null, 2)}</pre>
                </div>
                <div className="json-section">
                  <h4>season.json</h4>
                  <pre className="json-viewer">{JSON.stringify(server.seasonConfig, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface SessionSectionProps {
  label: string;
  duration: number;
  setDuration: (v: number) => void;
  hour: number;
  setHour: (v: number) => void;
  minute: number;
  setMinute: (v: number) => void;
  maxWaitBox: number;
  setMaxWaitBox: (v: number) => void;
  overtimeWait: number;
  setOvertimeWait: (v: number) => void;
  isRaceSession?: boolean;
  durationType?: 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS';
  setDurationType?: (v: 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS') => void;
  minWaitingPlayers?: number;
  setMinWaitingPlayers?: (v: number) => void;
  maxWaitingPlayers?: number;
  setMaxWaitingPlayers?: (v: number) => void;
}

interface PitStopRulesSectionProps {
  duration: number;
  durationType: 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS';
  mandatoryPitStop: boolean;
  setMandatoryPitStop: (v: boolean) => void;
  requiresTyreChange: boolean;
  setRequiresTyreChange: (v: boolean) => void;
  requiresRefuelling: boolean;
  setRequiresRefuelling: (v: boolean) => void;
  pitWindow: number;
  setPitWindow: (v: number) => void;
}

const PitStopRulesSection: React.FC<PitStopRulesSectionProps> = ({
  duration,
  durationType,
  mandatoryPitStop,
  setMandatoryPitStop,
  requiresTyreChange,
  setRequiresTyreChange,
  requiresRefuelling,
  setRequiresRefuelling,
  pitWindow,
  setPitWindow,
}) => {
  const durationSeconds = duration * (durationType === 'GameModeSelectionDuration_TIME' ? 60 : 1);
  const canEnable = durationType === 'GameModeSelectionDuration_TIME' && durationSeconds >= 1200;

  return (
    <div className="section session-section pit-stop-rules-section">
      <h3>Race Pit Stop Rules</h3>
      <div className="form-row compact-session-row">
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={mandatoryPitStop}
              disabled={!canEnable}
              onChange={(e) => setMandatoryPitStop(e.target.checked)}
            />
            Mandatory Pit Stop
          </label>
          {!canEnable && <small>Requires a time-based race of at least 20 minutes.</small>}
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={requiresTyreChange}
              disabled={!mandatoryPitStop}
              onChange={(e) => setRequiresTyreChange(e.target.checked)}
            />
            Mandatory Tyres
          </label>
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={requiresRefuelling}
              disabled={!mandatoryPitStop}
              onChange={(e) => setRequiresRefuelling(e.target.checked)}
            />
            Mandatory Fuel
          </label>
        </div>
        <div className="form-group">
          <label>Pit Window (sec)</label>
          <input
            type="number"
            value={pitWindow}
            disabled={!mandatoryPitStop}
            onChange={(e) => setPitWindow(Math.max(0, Math.min(parseInt(e.target.value || '0', 10), durationSeconds)))}
            min="0"
            max={durationSeconds}
          />
        </div>
      </div>
    </div>
  );
};

const SessionSection: React.FC<SessionSectionProps> = ({
  label,
  duration,
  setDuration,
  hour,
  setHour,
  minute,
  setMinute,
  maxWaitBox,
  setMaxWaitBox,
  overtimeWait,
  setOvertimeWait,
  isRaceSession,
  durationType,
  setDurationType,
  minWaitingPlayers,
  setMinWaitingPlayers,
  maxWaitingPlayers,
  setMaxWaitingPlayers,
}) => (
  <div className="section session-section">
    <h3>{label} Session</h3>

    {isRaceSession && setDurationType && (
      <div className="form-group race-type-group">
        <label>Race Type</label>
        <select
          value={durationType}
          onChange={(e) =>
            setDurationType(
              e.target.value as 'GameModeSelectionDuration_TIME' | 'GameModeSelectionDuration_LAPS'
            )
          }
        >
          <option value="GameModeSelectionDuration_TIME">Time-based (minutes)</option>
          <option value="GameModeSelectionDuration_LAPS">Laps-based</option>
        </select>
      </div>
    )}

    <div className="form-row compact-session-row">
      <div className="form-group compact-session-group">
        <label>Duration ({isRaceSession && durationType === 'GameModeSelectionDuration_LAPS' ? 'Laps' : 'minutes'})</label>
        <input
          type="number"
          className="compact-input"
          value={duration}
          onChange={(e) => setDuration(parseInt(e.target.value || '0', 10))}
          min="0"
        />
      </div>
      <div className="form-group compact-session-group">
        <label>Time of Day</label>
        <input
          type="time"
          className="compact-input compact-time-input"
          value={`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`}
          onChange={(e) => {
            const [nextHour, nextMinute] = e.target.value.split(':').map((value) => parseInt(value || '0', 10));
            setHour(Math.max(0, Math.min(23, nextHour || 0)));
            setMinute(Math.max(0, Math.min(59, nextMinute || 0)));
          }}
        />
      </div>
    </div>

    {isRaceSession && setMinWaitingPlayers && setMaxWaitingPlayers && (
      <div className="form-row compact-session-row">
        <div className="form-group">
          <label>Min Waiting Players</label>
          <input
            type="number"
            value={minWaitingPlayers}
            onChange={(e) => setMinWaitingPlayers(parseInt(e.target.value))}
            min="1"
          />
        </div>
        <div className="form-group">
          <label>Max Waiting Players</label>
          <input
            type="number"
            value={maxWaitingPlayers}
            onChange={(e) => setMaxWaitingPlayers(parseInt(e.target.value))}
            min="1"
          />
        </div>
      </div>
    )}

    <div className="form-row">
      <div className="form-group">
        <label>Max Wait to Box (sec)</label>
        <input
          type="number"
          value={maxWaitBox}
          onChange={(e) => setMaxWaitBox(parseInt(e.target.value))}
          min="0"
        />
      </div>
      <div className="form-group">
        <label>Overtime Wait Next Session (sec)</label>
        <input
          type="number"
          value={overtimeWait}
          onChange={(e) => setOvertimeWait(parseInt(e.target.value))}
          min="0"
        />
      </div>
    </div>
  </div>
);

export default ServerEditor;
